// The body blueprint driving a multi-region OGS splat: where a graph's play requests (one
// per Play Clip node) land when the mesh is a part rig rather than the GLB mixer. Nothing
// here renders.
// Notes: ~/comments/aos-threejs-poc/multipartBodyDrive_createPartRigDriver_arbitration.md

/** Body-clip rows for `slug`, as `{name, url}` the part rig can bind. The character's own
 *  animation assets. A fetch failure is loud and yields nothing. */
export async function loadBodyClipRows(slug, listAnimationAssets) {
  if (!slug) return []
  let rows
  try {
    rows = await listAnimationAssets(slug)
  } catch (e) {
    console.error(`[multipart] body clips unavailable — the graph will drive nothing: ${e?.message ?? e}`)
    return []
  }
  return (rows ?? [])
    .filter((row) => row?.kind === 'body' && (row.file_url ?? row.fileUrl))
    .map((row) => ({ name: row.name, url: row.file_url ?? row.fileUrl }))
}

/** Whether a graph holds anything past the mandatory, non-deletable finalPose node. */
export function isGraphAuthored(graph) {
  return !!graph?.nodes?.some((node) => node?.type !== 'finalPose')
}

/** Who poses the part rig this frame: `none | override | graph`. ONE owner — two would
 *  present the same splat twice. A preview beats the graph and gives it back on expiry. */
export function rigOwner({ drive, graph, directPlay, clipNames, now, override }) {
  if (!drive) return 'none'
  // `frame` resolves this once per frame; re-deriving it here would allocate again.
  const state = override === undefined ? directPlayState(directPlay, now) : override
  if (state && !state.expired && clipNames?.includes(state.clipName)) return 'override'
  return isGraphAuthored(graph) ? 'graph' : 'none'
}

/** The `__DIRECT_PLAY__` override as this rig reads it, or null. `expired` is reported,
 *  never acted on — releasing it runs the payload's hook, which is the caller's. */
export function directPlayState(directPlay, now) {
  if (!directPlay?.clipName) return null
  return {
    clipName: directPlay.clipName,
    baseClipName: directPlay.baseClipName ?? null,
    expired: now > (directPlay.expiresAt ?? 0),
  }
}

/** The wall-clock wedge past `expiresAt` for a followAction hold — the GLB path's guard. */
export const FOLLOW_ACTION_WEDGE_MS = 15000

/** The coarsest the override path will pose: 30 Hz, the rate a generated clip is baked at
 *  and the rate ARKit face data arrives at. A clip whose own frames are finer is posed on
 *  those instead. */
export const MAX_POSE_PERIOD_S = 1 / 30

/* The root is placed at the pose that is ACTUALLY ON SCREEN — no lead at all.
 *
 * A splat drive re-lifts ~1M gaussians per pose, so it lands whole frames after the clock
 * that asked for it. Any allowance for the root to run ahead of that is, by construction,
 * the character sliding over legs that are holding still: the foot on the ground moves
 * with the body for exactly as long as the lead permits. There is no value that avoids
 * this, only values that trade how much of it you get for how evenly the root advances —
 * measured on myragaussainsplatting at 76 fps, as the fraction of rendered frames on
 * which some foot is planted:
 *
 *     lead 50 ms  21% planted, skate 0.74   root never waits
 *     lead 16 ms  44% planted, skate 0.35   root waits on 12% of frames
 *     lead  0 ms  47% planted, skate 0.34   root waits on 14% of frames
 *
 * Zero is the deliberate choice, and it is a decision about which artefact is acceptable
 * rather than a tuning: the body may HOLD and then jump when the drive falls behind, and
 * it must never skate. That holds at any frame rate — a drive at 10 Hz steps the root in
 * 10 Hz jumps rather than gliding between its poses — which is the point, because a low
 * frame rate is exactly when a lead does the most damage. Root and legs on one clock is
 * the contract (generatedRootMotion.js); this is that contract with nothing subtracted.
 *
 * A drive that does NOT report a landed time (the GLB rig, where posing IS the frame, so
 * there is no lag to absorb) keeps its own clock and is unaffected.
 */

/** Whether a `followAction` hold has ended: when the CLIP has, on this rig's clock.
 *  `time` is the posed time once the clip owns the rig, else null; `duration` null means
 *  the rig cannot play it, so only the wall clock can end the hold. */
export function followActionExpired({ expiresAt }, now, time, duration) {
  if (duration == null) return now > (expiresAt ?? 0)
  if (time != null && time >= duration) return true
  return now > (expiresAt ?? 0) + FOLLOW_ACTION_WEDGE_MS
}

/** Weights for a direct play: the previewed clip alone at full weight. `baseClipName` is
 *  previewAnimationAsset's additive reconstruction, and its presence says so. */
export function directPlayWeights({ clipName, baseClipName }) {
  const layered = !!baseClipName && baseClipName !== clipName
  const weights = { [clipName]: 1 }
  const loop = { [clipName]: false }
  if (layered) {
    weights[baseClipName] = 1
    loop[baseClipName] = true
  }
  return { weights, options: { additive: { [clipName]: layered }, loop } }
}

/**
 * The direct play's blend envelope: 0 → 1 over `blendInMs` from `startTime`, back to 0
 * over the last `blendOutMs`. 1 when the payload declares neither, which is what a
 * preview from before this existed gets — an unchanged hard cut.
 *
 * The SAME formula GraphEvaluator.applyWeights runs for the GLB rig, including its
 * followAction rule: with one, the tail is measured in CLIP time (the clip's own last
 * `blendOutMs`), so a drive posing at 12 Hz or 50 fades over the same part of the motion
 * rather than over a wall-clock window that a slow frame walks straight past.
 *
 * @param {object} dp        the __DIRECT_PLAY__ payload
 * @param {number} now       wall clock, ms
 * @param {number} time      the clip time this frame poses at
 * @param {number|null} duration the clip's own length, seconds — null when the rig cannot play it
 */
export function directPlayEnvelope(dp, now, time, duration) {
  let weight = 1
  const blendInMs = dp?.blendInMs | 0
  const blendOutMs = dp?.blendOutMs | 0
  if (blendInMs > 0 && dp.startTime != null) {
    const ratio = (now - dp.startTime) / blendInMs
    if (ratio < weight) weight = ratio
  }
  if (blendOutMs > 0) {
    const ratio = dp.followAction && duration
      ? ((duration - time) * 1000) / blendOutMs
      : ((dp.expiresAt ?? 0) - now) / blendOutMs
    if (ratio < weight) weight = ratio
  }
  return weight < 0 ? 0 : (weight > 1 ? 1 : weight)
}

/**
 * The override at `weight`, the graph at its complement, into the caller's reusable
 * slots — the crossfade, rather than a cut to the previewed clip and a cut back to idle.
 *
 * The graph keeps being evaluated under an override (it always was, so the editor's
 * weight bars and the state machines' clocks do not freeze), so its weights are already
 * in hand; this is only the mixing. At weight 1 the graph contributes nothing and the
 * result is exactly what a hard override produced before.
 *
 * Writes in place: this runs per frame, and the no-per-frame-alloc rule applies here as
 * much as in GraphEvaluator (whose tail blend reuses `_fadeWeights` for the same reason).
 */
export function blendOverrideWithGraph(out, slots, weight, graph) {
  for (const k in out.weights) delete out.weights[k]
  for (const k in out.additive) delete out.additive[k]
  for (const k in out.loop) delete out.loop[k]
  for (const k in out.speed) delete out.speed[k]
  const complement = 1 - weight
  if (complement > 0 && graph?.weights) {
    for (const name in graph.weights) {
      const w = graph.weights[name] * complement
      if (!(w > 0)) continue
      out.weights[name] = w
      if (graph.additive?.[name] !== undefined) out.additive[name] = graph.additive[name]
      if (graph.loop?.[name] !== undefined) out.loop[name] = graph.loop[name]
      if (graph.speed?.[name] !== undefined) out.speed[name] = graph.speed[name]
    }
  }
  // The override last: where both name a clip, the override owns it.
  for (const name in slots.weights) {
    const w = slots.weights[name] * weight
    if (w > 0) out.weights[name] = w
    else delete out.weights[name]
    const additive = slots.options?.additive?.[name]
    if (additive !== undefined) out.additive[name] = additive
    const loop = slots.options?.loop?.[name]
    if (loop !== undefined) out.loop[name] = loop
  }
  return out
}

/** Lay the body-gesture envelope over the graph's weights, in place — the same
 *  GestureChannel the GLB path plays. Returns the clip laid on, or null. */
export function layerGesture(weights, additive, loop, envelope, clipNames) {
  if (!weights || !envelope || envelope.done || !(envelope.weight > 0)) return null
  if (!clipNames?.includes(envelope.clipName)) return null
  weights[envelope.clipName] = envelope.weight
  additive[envelope.clipName] = true
  loop[envelope.clipName] = false
  return envelope.clipName
}

/** Clip names the graph's play requests ask for that the rig cannot play — named, never
 *  skipped, each once. Allocation-free in the steady state: this runs per frame. */
export function unplayableClips(plays, clipNames) {
  let missing = null
  for (let k = 0; k < (plays?.length ?? 0); k++) {
    const name = plays[k].clipName
    if (!((plays[k].weight ?? 0) > 0) || clipNames?.includes(name) || missing?.includes(name)) continue
    (missing ??= []).push(name)
  }
  return missing ?? EMPTY
}

const EMPTY = Object.freeze([]) // the steady-state return, so never mutated

/** The per-frame drive: pick an owner, weight the rig, pose it. Split out of useFrame so
 *  it runs headlessly against a real PartRigDrive; side effects are `rig.setWeights` and
 *  `drive.driveAt`. What `evaluate()` means is the caller's business. */
export function createPartRigDriver({ rig, drive, evaluate, warn = console.warn, info = console.info }) {
  // Two clocks: setTime is absolute and shared, so a preview would open mid-motion.
  let graphClock = 0
  let overrideClock = 0
  let owner = 'none'
  let overrideClip = null
  let lastAsked = null
  let overrideSlots = null
  // Reusable mixing slots — `frame` runs every render frame (blendOverrideWithGraph).
  const mixed = { weights: {}, additive: {}, loop: {}, speed: {} }
  // What plays on the clip TEMPLATES besides a preview: the gesture. The graph's own clips
  // play on per-node players (rig.setPlays) and never pass through here.
  const templ = { weights: {}, additive: {}, loop: {}, speed: {} }
  const warned = { preview: null, gesture: null, unplayable: null }

  // Takes the caller's list — clipNames() allocates and `frame` already holds one.
  const warnUnbound = (kind, clipName, clipNames) => {
    if (!clipName || warned[kind] === clipName || clipNames.includes(clipName)) return
    warned[kind] = clipName
    warn(`[multipart] ${kind} clip '${clipName}' is not bound to the part rig — the graph keeps driving`)
  }

  return {
    get owner() { return owner },

    frame({ delta = 0, now, graph, directPlay = null, envelope = null }) {
      const clipNames = rig.clipNames()
      const override = directPlayState(directPlay, now)
      if (override && directPlay.followAction) {
        // The clip may be posed long after the payload was set — its own clock decides.
        const time = overrideClip === override.clipName ? overrideClock : null
        override.expired = followActionExpired(directPlay, now, time, rig.clipDuration(override.clipName))
      }
      if (override && !override.expired) warnUnbound('preview', override.clipName, clipNames)

      owner = rigOwner({ drive, graph, clipNames, override })
      const result = { owner, expiredDirectPlay: !!override?.expired, clearGesture: !!envelope?.done }
      if (owner === 'none') {
        drive.driveAt(null) // nobody poses the rig this frame — a pending recolour runs on its own
        return result
      }

      const plays = evaluate()?.plays ?? EMPTY
      for (const k in templ.weights) delete templ.weights[k]
      for (const k in templ.additive) delete templ.additive[k]
      for (const k in templ.loop) delete templ.loop[k]
      if (envelope && !envelope.done && !layerGesture(templ.weights, templ.additive, templ.loop, envelope, clipNames)) {
        warnUnbound('gesture', envelope.clipName, clipNames)
      }

      if (owner === 'override') {
        if (overrideClip !== override.clipName) {
          overrideClock = 0 // from the clip's start, not the graph's clock
          overrideClip = override.clipName
          overrideSlots = directPlayWeights(override)
          // The bone count is what tells a partial bind from a broken preview.
          info(`[multipart] preview '${override.clipName}' owns the part rig — `
            + `${rig.boundTrackCount(override.clipName)} bones, `
            + `${rig.clipDuration(override.clipName)?.toFixed(2)}s`
            + `${override.baseClipName ? `, over '${override.baseClipName}'` : ', exclusive'}`)
        } else {
          overrideClock += delta
        }
        // Crossfade with whatever the graph is driving (idle, usually), rather than
        // cutting to the clip and cutting back. The clip's own duration decides the tail
        // under followAction, so the blend sits at the END of the motion however fast the
        // drive is posing.
        const duration = rig.clipDuration(override.clipName)
        // `blend`, not `envelope`: that name is the GESTURE envelope's, one scope up.
        const blend = directPlayEnvelope(directPlay, now, overrideClock, duration)
        result.weight = blend
        if (blend >= 1) {
          rig.setWeights(overrideSlots.weights, overrideSlots.options)
        } else {
          // The gesture fades out with the graph, at the complement.
          blendOverrideWithGraph(mixed, overrideSlots, blend, templ)
          rig.setWeights(mixed.weights, mixed)
        }
        // The graph's own players at the complement — released outright at full weight,
        // so the graph starts fresh when it takes the rig back.
        rig.setPlays(plays, delta, 1 - blend)
        // Pose on the CLIP'S OWN frames, and never twice on the same one.
        //
        // A generated clip is 30 fps, so a drive posing as fast as it can spends most of
        // its lifts interpolating between frames that already exist — and because the root
        // is placed at the pose that landed (no lead, see above), those extra poses do not
        // smooth the travel. They only make the landings uneven, which IS the stutter:
        // measured on myragaussainsplatting, posing freely gave root steps of 23.6 mm with
        // a standard deviation of 12.7 (CV 0.48, up to 76 mm), so her apparent speed
        // changed frame to frame. On the clip's own frames each step is one frame of
        // travel and they come out uniform — 46.7 mm, CV 0.16.
        //
        // Skipping the repeat is what buys the regularity: re-posing the same clip time
        // costs a full lift and present for identical data, and leaves the pipeline no
        // idle time to land the NEXT frame on schedule.
        // Never SLOWER than MAX_POSE_PERIOD_S: a clip's keyframes can be sparse (an
        // authored one may carry two over two seconds) and there the mixer's
        // interpolation IS the animation — posing only on its keys would play it as a
        // slideshow. So this is "the clip's own rate, floored at 30 Hz", not "its keys".
        const period = Math.min(rig.clipFramePeriod?.(override.clipName) ?? MAX_POSE_PERIOD_S, MAX_POSE_PERIOD_S)
        // Advance only when the drive TOOK the frame. `driveAt` returns false when it did
        // not — the multipart slot is busy with a pose or an appearance recolour
        // (splatFrameSlot.js) — and claiming that frame anyway means the next ask waits
        // for the CLOCK to cross into the following one, which at 75 fps over a 30 fps
        // clip is ~2.5 render frames of doing nothing per refusal. A drive that reports
        // nothing (the GLB shape, and any async one) is taken as having taken it.
        const asked = Math.floor(overrideClock / period) * period
        if (asked !== lastAsked && drive.driveAt(asked) !== false) lastAsked = asked
        result.time = overrideClock
        // How far into the clip this frame posed — what a root motion is placed at.
        //
        // The time the pose LANDED, when the drive reports one. On the GLB rig the two
        // are the same number: posing is the mixer writing bone matrices, so it is done
        // by the time the frame ends. A splat's is not — the drive re-lifts ~1M
        // gaussians and presents, which lands whole frames later — and placing the root
        // at the wall clock meanwhile is what makes a walk SKATE: the character slides
        // on smoothly while its legs step at whatever rate the lift manages. Root and
        // legs on one clock is the contract (generatedRootMotion.js); this is what
        // honours it when the two cadences differ.
        // A drive that reports the landed time is asked; one that does not (the GLB
        // shape, where posing IS the frame) keeps its own clock. Null from a drive that
        // DOES report means no pose has landed yet, which is a root that has not moved —
        // not a licence to fall back to the clock.
        const landed = drive.posedTime ? drive.posedTime() : null
        // Strictly the landed time: no lead, so the root cannot advance over legs that
        // have not moved. `min` is defensive — a landed time never exceeds the clock that
        // asked for it — and null means no pose has landed, which is a root that has not
        // moved, not a licence to fall back to the clock.
        const at = !drive.posedTime ? overrideClock
          : (landed == null ? 0 : Math.min(overrideClock, landed))
        result.progress = duration ? Math.min(1, at / duration) : 1
        return result
      }

      overrideClip = null
      lastAsked = null
      const unplayable = unplayableClips(plays, clipNames)
      if (unplayable.length) {
        const missing = unplayable.join(',')
        if (warned.unplayable !== missing) {
          warned.unplayable = missing
          warn(`[multipart] body graph asks for clips this character has not uploaded: ${missing}`)
        }
      }
      rig.setWeights(templ.weights, templ)   // the gesture, if any; every other template stops
      rig.setPlays(plays, delta, 1)
      graphClock += delta
      drive.driveAt(graphClock)
      result.time = graphClock
      return result
    },
  }
}
